import { useEffect, useState } from 'react';
import { windowExists } from '../../../common/utils/AccountUtils';

const refreshQualtricsLoader = () => {
  if (!windowExists()) return;

  const unload = () => window.QSI?.API?.unload?.();
  const load = () => window.QSI?.API?.load?.();
  const run = () => window.QSI?.API?.run?.();

  unload();
  load();
  run();
};

export const triggerSweepstakesQualtricsSurvey = ({ isOpen, surveyData }) => {
  // Trigger for activating the Qualtrics survey
  // IMPORTANT: Should happen AFTER api response confirming opt-in
  if (isOpen) sessionStorage.setItem('showSweepstakesQualtricsSurvey', 'true');
  else sessionStorage.removeItem('showSweepstakesQualtricsSurvey');

  if (isOpen) {
    // Option 1: all in one
    sessionStorage.setItem('sweepstakesQualtricsSurveyPack', JSON.stringify(surveyData));
    // Option 2: individual items
    sessionStorage.setItem('sweepstakesQualtricsFirstName', surveyData.firstName);
    sessionStorage.setItem('sweepstakesQualtricsLastName', surveyData.lastName);
    sessionStorage.setItem('sweepstakesQualtricsEmail', surveyData.email);
    sessionStorage.setItem('sweepstakesQualtricsPhone', surveyData.phone);
    sessionStorage.setItem('sweepstakesQualtricsSVOC_ID', surveyData.SVOC_ID);
    sessionStorage.setItem('sweepstakesQualtricsUSER_ID', surveyData.USER_ID);
    // Option 3: event bus trigger
    // eslint-disable-next-line max-len
    // To consume:window.LIFE_CYCLE_EVENT_BUS.lifeCycle.events["loyalty-benefits"]["show-qualtrics-sweepstakes-survey"].subject._events
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.show-qualtrics-sweepstakes-survey', { surveyData });

    // Option 4: Store as cookies
    document.cookie = `showSweepstakesQualtricsSurvey=${true}; path=/`;
    document.cookie = `sweepstakesQualtricsSurveyPack=${encodeURIComponent(JSON.stringify(surveyData))}; path=/`;

    refreshQualtricsLoader();
  } else {
    [
      'sweepstakesQualtricsSurveyPack',
      'sweepstakesQualtricsFirstName',
      'sweepstakesQualtricsLastName',
      'sweepstakesQualtricsEmail',
      'sweepstakesQualtricsPhone',
      'sweepstakesQualtricsSVOC_ID',
      'sweepstakesQualtricsUSER_ID'
    ].forEach((key) => sessionStorage.removeItem(key));
  }
};

/**
 * Clean up Qualtrics survey content from DOM
 * Removes QSI elements and clears survey container
 */
export const cleanupQualtricsContent = () => {
  if (!windowExists()) {
    return;
  }

  // Clear the main survey container
  const qualtricsContainer = document.querySelector('#sweepstakes-qualtrics-survey');
  if (qualtricsContainer) {
    qualtricsContainer.innerHTML = '';
  }

  // Remove all QSIUserDefinedHTML elements and their containers
  const qsiElements = document.querySelectorAll('.QSIUserDefinedHTML, [class*="QSI"]');
  qsiElements.forEach((element) => {
    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }
  });

  // Also try to unload Qualtrics if available
  if (window.QSI?.API?.unload) {
    window.QSI.API.unload();
  }
};

/**
 * Custom hook to handle Qualtrics survey completion events
 * @param {Function} onSurveyComplete - Callback function when survey is completed
 * @param {boolean} isActive - Whether to listen for survey completion events
 */
export const useSurveyCompletion = (onSurveyComplete, isActive = true) => {
  useEffect(() => {
    if (!isActive || !windowExists()) {
      return undefined;
    }

    const handleSurveyCompletion = (event) => {
      // Check if the message is from Qualtrics completion (external iframe)
      if (event?.data?.message === 'endOfSurvey' && event?.origin?.includes('qualtrics.com') && onSurveyComplete) {
        // Survey completed - trigger callback
        onSurveyComplete(event);
      }
    };

    // Add event listener
    window.addEventListener('message', handleSurveyCompletion, false);

    // Cleanup function
    return () => window.removeEventListener('message', handleSurveyCompletion, false);
  }, [onSurveyComplete, isActive]);
};

// Fallback image constants
const FALLBACK_HERO_IMAGE = 'https://placeholder.pics/svg/957x262/7a3de7-f07153/ffffff/%20';

/**
 * Generic image error hook. Accepts an optional `src` to reset state when source changes.
 * Use semantic wrappers without arguments for clarity.
 */
export const useImageError = () => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, []);

  const handleError = (event) => {
    const img = event.currentTarget;
    // eslint-disable-next-line no-param-reassign
    img.onerror = null;
    setHasError(true);
  };

  return { hasError, handleError };
};

// Note: callers should import and use `useImageError` directly.

/**
 * Handle hero image errors with fallback
 * @param {Event} event - The image error event
 */
export const handleHeroImageError = (event) => {
  const img = event.currentTarget;
  // eslint-disable-next-line no-param-reassign
  img.onerror = null;
  img.src = FALLBACK_HERO_IMAGE;
};

/**
 * Custom hook to handle sweepstakes logo image errors with Badge icon fallback
 * @param {string} logoUrl - The URL of the logo image
 * @returns {Object} - { hasError, handleError } for managing logo state
 */
// Previously exported wrapper `useSweepstakesLogo` removed — callers should use `useImageError`.
